<template>
    <div class="h-100 position-relative">
        <LMap :center="centerLatLng"
              :zoom="zoom"
              :options="{zoomSnap: 0.25, preferCanvas: true, zoomControl: false, maxZoom: 21}"
              class="h-100"
              ref="map"
              @update:zoom="updateZoom"
              @update:bounds="boundsChanged"
              @ready="onMapReady">
            <LControlLayers position="topright" @ready="onLayersControlReady"></LControlLayers>
            <MapyCzBasicLayer v-if="tilesReady"/>
            <MapyCzOrthoPhotoLayer :visible="false" v-if="tilesReady"/>
            <LControlZoom position="topright"/>
        </LMap>
        <div class="points-map-filters d-flex flex-wrap align-items-start gap-2" v-if="searchControl" :class="{'points-map-filters-hidden': activeMarker !== null}">
            <div class="points-map-field">
                <EstateTypeControl :estateTypes="estateTypes" emptyLabel="Všechny typy nemovitostí" @change="estateTypeChange"/>
            </div>
            <div class="points-map-field" v-if="estateType === 'land' && landTypes.length > 0">
                <LandTypeControl :landTypes="landTypes" @change="landTypeChange"/>
            </div>
            <div class="points-map-field points-map-field-dates">
                <DateRangeControl @change="dateRangeChange" :defaultDateRange="dateRange"/>
            </div>
            <div class="points-map-field points-map-field-location">
                <LocationSearchControl :value="null" @input="updateCenterPoint" placeholder="Hledat lokalitu..."/>
            </div>
        </div>
        <div class="map-zoom-level">Přiblížení {{ zoomLabel }}</div>
        <div class="points-map-hint" v-if="hint !== null">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <span v-else-if="zoomTooSmall"><i class="fas fa-search-plus me-2"></i></span>
            {{ hint }}
        </div>
        <div class="map-legend" v-if="legendItems.length > 0">
            <div class="map-legend-content">
                <div v-for="legendItem in legendItems"
                     :key="legendItem.label"
                     class="legend-item">
                    <span class="legend-marker" :style="{backgroundColor: legendItem.color}"></span>
                    <span class="legend-label">{{ legendItem.label }}</span>
                </div>
            </div>
        </div>
        <div v-if="activeMarker !== null" class="points-map-panel card shadow mb-0">
            <div class="card-header d-flex align-items-center justify-content-between py-2">
                <span class="fw-semibold">Na tomto místě <span class="text-muted fw-normal">· {{ activeMarker.points.length }}</span></span>
                <button type="button" class="btn-close" aria-label="Zavřít" @click="closePanel"></button>
            </div>
            <div class="list-group list-group-flush overflow-auto">
                <a v-for="point in activeMarker.points"
                   :key="point.link"
                   href="javascript:"
                   class="list-group-item list-group-item-action d-flex align-items-start gap-2 py-2"
                   @click="() => pointClicked(point)">
                    <span class="legend-marker mt-1" :style="{backgroundColor: point.color}"></span>
                    <span class="flex-fill min-w-0" v-html="point.name"></span>
                </a>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import DateRangeControl, {DateRange} from '@/components/common/DateRangeControl.vue';
import EstateTypeControl from '@/components/common/EstateTypeControl.vue';
import LandTypeControl, {LandTypeOption} from '@/components/common/LandTypeControl.vue';
import LocationSearchControl from '@/components/common/LocationSearchControl.vue';
import {DATE_FORMAT} from '@/components/DatePicker';
import {canvasMarker} from '@/components/maps/CanvasMarker';
import MapyCzBasicLayer from '@/components/maps/MapyCzBasicLayer.vue';
import MapyCzOrthoPhotoLayer from '@/components/maps/MapyCzOrtophotoLayer.vue';
import {EstateType, Location} from '@/model/api';
import {log} from '@/model/logger';
import {concat, NonEmptyArray} from 'fp-ts/es6/NonEmptyArray';
import L from 'leaflet';
import {publishLeafletMap, unpublishLeafletMap} from '@/shared/leafletMapRegistry';
import moment from 'moment/moment';
import naja from 'naja';
import {computed, defineComponent, onBeforeUnmount, PropType, ref, shallowRef, watch} from 'vue';
import {LControlLayers, LControlZoom, LMap} from '@vue-leaflet/vue-leaflet';

interface Point {
    readonly deduplicationKey: string | null;
    readonly coordinates: {
        readonly latitude: number;
        readonly longitude: number;
    };
    readonly name: string;
    readonly link: string;
    readonly isLinkAjax: boolean;
    readonly color: string;
}

/** The points sharing one spot, drawn as a single marker. */
interface Marker {
    readonly key: string;
    readonly colors: NonEmptyArray<string>;
    readonly points: NonEmptyArray<Point>;
    readonly coordinates: L.LatLngTuple;
}

interface Coordinates {
    readonly latitude: number;
    readonly longitude: number;
}

interface LegendItem {
    readonly label: string;
    readonly color: string;
}

const MIN_ZOOM = 14;
const DEFAULT_ZOOM = 16;
const MAX_CACHED_MARKERS = 5000;
const DEBOUNCE_MS = 500;
const GEOLOCATION_TIMEOUT = 5000;
const GEOLOCATION_MAX_AGE = 60000;

/** Roughly one footprint for every marker; a two- or three-digit count gets a little more room. */
function markerSize(count: number): number {
    if (count < 10) {
        return 20;
    }

    return count < 100 ? 24 : 28;
}

/** Space around the disc for the halo of the active marker and the badge of a mixed group. */
const MARKER_MARGIN = 4;

function czechPlural(count: number, one: string, few: string, many: string): string {
    if (count === 1) {
        return one;
    }

    return count >= 2 && count <= 4 ? few : many;
}

export default defineComponent({
    components: {
        MapyCzOrthoPhotoLayer,
        MapyCzBasicLayer,
        LocationSearchControl,
        LMap,
        LControlLayers,
        LControlZoom,
        DateRangeControl,
        EstateTypeControl,
        LandTypeControl,
    },
    props: {
        center: {
            type: Object as PropType<Coordinates>,
            required: true,
        },
        providerUrl: {
            type: String,
            required: true,
        },
        searchControl: {
            type: Boolean,
            default: false,
        },
        legendItems: {
            type: Array as PropType<LegendItem[]>,
            required: true,
        },
        estateTypes: {
            type: Array as PropType<EstateType[]>,
            default: true,
        },
        landTypes: {
            type: Array as PropType<LandTypeOption[]>,
            default: () => [],
        },
        defaultDateRange: {
            type: Object as PropType<DateRange>,
        },
        useGeolocation: {
            type: Boolean,
            default: false,
        },
        // Null: DEFAULT_ZOOM.
        initialZoom: {
            type: Number as PropType<number | null>,
            default: null,
        },
    },
    setup(props) {
        const map = ref<any>(null);
        const leafletMap = ref<L.Map | null>(null);
        const zoom = ref(props.initialZoom ?? DEFAULT_ZOOM);
        const loading = ref(false);
        const activeMarker = ref<Marker | null>(null);
        const tilesReady = ref(!props.useGeolocation);
        const dateRange = shallowRef<DateRange>(props.defaultDateRange as DateRange);
        const estateType = ref<EstateType | null>(null);
        const landType = ref<string | null>(null);
        // the loaded area shows no marker at all — worth telling apart from "still loading"
        const emptyArea = ref(false);

        let loadedBoundsAreas: L.LatLngBounds[] = [];
        let previousAbortController: AbortController | null = null;
        const previousMarkers: Map<string, [Marker, L.Path]> = new Map();
        let refreshDebounceTimer: number | null = null;
        let isAnimating = false;

        const centerLatLng = computed((): L.LatLng => {
            return L.latLng(props.center.latitude, props.center.longitude);
        });

        const zoomTooSmall = computed((): boolean => {
            return zoom.value < MIN_ZOOM;
        });

        // zoomSnap is 0.25, so the level is not always a whole number.
        const zoomLabel = computed((): string => {
            return zoom.value.toLocaleString('cs-CZ', {maximumFractionDigits: 2});
        });

        const hint = computed((): string | null => {
            if (zoomTooSmall.value) {
                const levels = Math.ceil(MIN_ZOOM - zoom.value);

                return `Pro zobrazení bodů přibližte mapu ještě o ${levels} ${czechPlural(levels, 'úroveň', 'úrovně', 'úrovní')}`;
            }

            if (loading.value) {
                return 'Načítání…';
            }

            if (emptyArea.value) {
                return 'V této oblasti a období nejsou žádné záznamy';
            }

            return null;
        });

        const legendLabelsByColor = computed((): Map<string, string> => {
            return new Map(props.legendItems.map(item => [item.color, item.label]));
        });

        function updateCenterPoint(location: Location | null): void {
            if (location === null || location.center === null) {
                return;
            }

            leafletMap.value!.flyTo(
                [location.center.coordinates[1], location.center.coordinates[0]],
                leafletMap.value!.getZoom(),
                {
                    duration: 0,
                    animate: false,
                },
            );
        }

        function updateZoom(newZoom: number): void {
            zoom.value = newZoom;
        }

        function tryUseUserLocation(): boolean {
            if (!props.useGeolocation) {
                return false;
            }

            if (!navigator.geolocation) {
                return false;
            }

            isAnimating = true;

            navigator.geolocation.getCurrentPosition(
                (position) => handleGeolocationSuccess(position),
                (error) => handleGeolocationError(error),
                {
                    timeout: GEOLOCATION_TIMEOUT,
                    maximumAge: GEOLOCATION_MAX_AGE,
                },
            );

            return true;
        }

        function handleGeolocationSuccess(position: GeolocationPosition): void {
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;

            if (!isFinite(latitude) || !isFinite(longitude)) {
                log('Invalid geolocation coordinates:', {latitude, longitude});
                isAnimating = false;
                tilesReady.value = true;
                doRefreshMarkers();
                return;
            }

            const userLocation = L.latLng(latitude, longitude);

            if (!leafletMap.value) {
                log('Map not ready for setView');
                isAnimating = false;
                tilesReady.value = true;
                doRefreshMarkers();
                return;
            }

            leafletMap.value.setView(userLocation, DEFAULT_ZOOM, {animate: false});
            isAnimating = false;
            tilesReady.value = true;
            doRefreshMarkers();
        }

        function handleGeolocationError(error: GeolocationPositionError): void {
            log('Geolocation error:', error.message);
            isAnimating = false;
            tilesReady.value = true;
            doRefreshMarkers();
        }

        function boundsChanged(): void {
            updateEmptyArea();
            tryRefreshMarkers();
        }

        function tryRefreshMarkers(): void {
            if (isAnimating) {
                return;
            }

            if (zoom.value < MIN_ZOOM || isAreaLoaded(leafletMap.value?.getBounds())) {
                if (refreshDebounceTimer !== null) {
                    window.clearTimeout(refreshDebounceTimer);
                    refreshDebounceTimer = null;
                }

                return;
            }

            if (refreshDebounceTimer !== null) {
                window.clearTimeout(refreshDebounceTimer);
            }

            refreshDebounceTimer = window.setTimeout(() => {
                doRefreshMarkers();
            }, DEBOUNCE_MS);
        }

        async function doRefreshMarkers(): Promise<void> {
            const abortController = new AbortController();

            try {
                loading.value = true;
                previousAbortController?.abort();
                previousAbortController = abortController;
                await refreshMarkers(abortController.signal);
            } catch (error) {
                if (!abortController.signal.aborted) {
                    log(error instanceof Error ? error.message : String(error));
                }
            } finally {
                // A newer request may still be running; only its own completion may hide the spinner.
                if (previousAbortController === abortController) {
                    loading.value = false;
                    updateEmptyArea();
                }
            }
        }

        function updateEmptyArea(): void {
            const bounds = leafletMap.value?.getBounds();

            if (!bounds || zoomTooSmall.value || !isAreaLoaded(bounds)) {
                emptyArea.value = false;
                return;
            }

            let visible = 0;

            previousMarkers.forEach(([marker]) => {
                if (bounds.contains(marker.coordinates)) {
                    visible++;
                }
            });

            emptyArea.value = visible === 0;
        }

        function markerClicked(marker: Marker): void {
            if (marker.points.length > 1) {
                activeMarker.value = marker;
                return;
            }

            pointClicked(marker.points[0]);
        }

        function closePanel(): void {
            activeMarker.value = null;
        }

        // the marker whose points are listed in the panel is drawn with a halo
        watch(activeMarker, (current, previous) => {
            if (previous !== null) {
                redrawMarker(previous.key);
            }

            if (current !== null) {
                redrawMarker(current.key);
            }
        });

        function pointClicked(point: Point): void {
            if (point.isLinkAjax) {
                naja.makeRequest('GET', point.link, null, {history: false});
            } else {
                window.open(point.link, '_blank');
            }
        }

        function dateRangeChange(newDateRange: DateRange): void {
            clearCache();
            dateRange.value = newDateRange;
            doRefreshMarkers();
        }

        function estateTypeChange(newEstateType: EstateType | null): void {
            clearCache();
            estateType.value = newEstateType;
            landType.value = null;
            doRefreshMarkers();
        }

        function landTypeChange(newLandType: string | null): void {
            clearCache();
            landType.value = newLandType;
            doRefreshMarkers();
        }

        function clearCache(): void {
            loadedBoundsAreas = [];
            removeAllMarkers();
        }

        watch(() => props.providerUrl, () => {
            clearCache();
        });

        async function refreshMarkers(signal: AbortSignal): Promise<void> {
            const bounds = leafletMap.value!.getBounds();

            if (zoom.value < MIN_ZOOM) {
                removeAllMarkers();
                return;
            }

            if (isAreaLoaded(bounds)) {
                return;
            }

            const response = await fetch(
                props.providerUrl.replace(
                    encodeURIComponent('{bounding_box}'),
                    JSON.stringify({
                        southWest: latLngToCoordinates(bounds.getSouthWest()),
                        northEast: latLngToCoordinates(bounds.getNorthEast()),
                    }),
                ).replace(encodeURIComponent('{estate_type}'), JSON.stringify(estateType.value))
                    .replace(encodeURIComponent('{land_type}'), JSON.stringify(landType.value))
                    .replace(
                        encodeURIComponent('{date_range}'),
                        JSON.stringify({
                            start: moment(dateRange.value.start).format(DATE_FORMAT),
                            end: moment(dateRange.value.end).format(DATE_FORMAT),
                        })),
                {signal},
            );

            if (!response.ok) {
                throw {
                    message: 'Point refresh resulted in error',
                    response,
                };
            }

            const points = await response.json() as Point[];
            const markers = combineToMarkers(points);

            evictCacheIfNeeded(markers.size);
            addNewMarkers(markers);
            expandLoadedBounds(bounds);
        }

        function latLngToCoordinates(coordinates: L.LatLng): Coordinates {
            return {
                longitude: coordinates.lng,
                latitude: coordinates.lat,
            };
        }

        function addNewMarkers(markers: Map<string, Marker>): void {
            let addedCount = 0;

            for (const [key, marker] of markers.entries()) {
                if (previousMarkers.has(key)) {
                    continue;
                }

                drawMarker(marker, false);
                addedCount++;
            }

            log(`Added ${addedCount} new markers, total ${previousMarkers.size}`);
        }

        function drawMarker(marker: Marker, active: boolean): void {
            const size = markerSize(marker.points.length) + MARKER_MARGIN * 2;
            const layer = canvasMarker(
                marker.coordinates,
                {
                    img: {
                        url: markerIconUrl(marker, active),
                        size: [size, size],
                    },
                },
            );

            layer.bindTooltip(markerTooltip(marker), {direction: 'top', offset: [0, -size / 2], className: 'points-map-tooltip'});
            layer.on('click', (e: L.LeafletMouseEvent) => {
                L.DomEvent.stopPropagation(e);
                markerClicked(marker);
            });
            layer.addTo(leafletMap.value! as L.Map);
            previousMarkers.set(marker.key, [marker, layer]);
        }

        function redrawMarker(key: string): void {
            const entry = previousMarkers.get(key);

            if (entry === undefined) {
                return;
            }

            const [marker, layer] = entry;

            layer.remove();
            drawMarker(marker, activeMarker.value?.key === key);
        }

        function evictCacheIfNeeded(incomingCount: number): void {
            if (previousMarkers.size + incomingCount > MAX_CACHED_MARKERS) {
                log(
                    `Cache limit reached (${previousMarkers.size} + ${incomingCount} > ${MAX_CACHED_MARKERS}), clearing cache`,
                );
                removeAllMarkers();
                loadedBoundsAreas = [];
            }
        }

        function removeAllMarkers(): void {
            previousMarkers.forEach(([, point]) => {
                point.remove();
            });
            previousMarkers.clear();
            activeMarker.value = null;
        }

        function isAreaLoaded(bounds: L.LatLngBounds | undefined): boolean {
            if (!bounds) {
                return false;
            }

            return loadedBoundsAreas.some((loadedBounds) => loadedBounds.contains(bounds));
        }

        function expandLoadedBounds(newBounds: L.LatLngBounds): void {
            const newBoundsObj = L.latLngBounds(newBounds.getSouthWest(), newBounds.getNorthEast());

            for (const existingBounds of loadedBoundsAreas) {
                if (boundsOverlapOrAdjacent(existingBounds, newBoundsObj)) {
                    existingBounds.extend(newBoundsObj);
                    return;
                }
            }

            loadedBoundsAreas.push(newBoundsObj);
        }

        function boundsOverlapOrAdjacent(
            bounds1: L.LatLngBounds, bounds2: L.LatLngBounds,
        ): boolean {
            const margin = 0.01;
            const extended = L.latLngBounds(
                L.latLng(bounds1.getSouth() - margin, bounds1.getWest() - margin),
                L.latLng(bounds1.getNorth() + margin, bounds1.getEast() + margin),
            );

            return extended.intersects(bounds2);
        }

        function coordinatesKey(coordinates: L.LatLngTuple): string {
            return `${coordinates[0]}_${coordinates[1]}`;
        }

        function combineToMarkers(points: Point[]): Map<string, Marker> {
            const markers = new Map<string, Marker>();

            points.forEach(point => {
                const coordinates: L.LatLngTuple = [point.coordinates.latitude, point.coordinates.longitude];
                const key = point.deduplicationKey ?? coordinatesKey(coordinates);
                const marker = markers.get(key);
                const color = point.color;

                if (!marker) {
                    markers.set(
                        key,
                        {
                            key,
                            coordinates,
                            colors: [color],
                            points: [point],
                        },
                    );
                } else {
                    markers.set(
                        key,
                        {
                            ...marker,
                            points: concat(marker.points, [point]),
                            colors: marker.colors.includes(color) ? marker.colors : [color, ...marker.colors],
                        },
                    );
                }
            });

            markers.forEach((marker, key) => markers.set(key, {...marker, points: orderPoints(marker.points)}));

            return markers;
        }

        /**
         * The points of a marker as the list shows them: by the legend order of their
         * colour first (the legend names the states from the best down), then by name.
         */
        function orderPoints(points: NonEmptyArray<Point>): NonEmptyArray<Point> {
            const legendOrder = new Map(props.legendItems.map((item, index) => [item.color, index]));
            const rank = (point: Point): number => legendOrder.get(point.color) ?? legendOrder.size;
            const text = (point: Point): string => point.name.replace(/<[^>]*>/g, ' ').trim();

            return [...points].sort((a, b) =>
                rank(a) - rank(b) || text(a).localeCompare(text(b), 'cs', {numeric: true}),
            ) as unknown as NonEmptyArray<Point>;
        }

        /** How many points of each colour the marker stands for, in the order of the legend. */
        function countsByColor(marker: Marker): Map<string, number> {
            const counts = new Map<string, number>();

            props.legendItems.forEach(item => counts.set(item.color, 0));
            marker.points.forEach(point => counts.set(point.color, (counts.get(point.color) ?? 0) + 1));

            return new Map([...counts.entries()].filter(([, count]) => count > 0));
        }

        function markerTooltip(marker: Marker): string {
            const count = marker.points.length;

            if (count === 1) {
                return marker.points[0].name;
            }

            const parts = [`<strong>${count} ${czechPlural(count, 'záznam', 'záznamy', 'záznamů')}</strong>`];

            countsByColor(marker).forEach((colorCount, color) => {
                const label = legendLabelsByColor.value.get(color);

                if (label !== undefined) {
                    parts.push(`${colorCount}× ${label.toLowerCase()}`);
                }
            });

            return parts.join(' · ');
        }

        /**
         * The marker as an SVG data URL: a disc in the colour of the points — the dominant
         * colour of a mixed group, with a badge of the other colour on its rim — carrying
         * the count of a group; the active marker gets a halo.
         */
        function markerIconUrl(marker: Marker, active: boolean): string {
            const count = marker.points.length;
            const size = markerSize(count);
            const canvas = size + MARKER_MARGIN * 2;
            const center = canvas / 2;
            const radius = size / 2 - 1;
            const colors = [...countsByColor(marker).entries()].sort(([, a], [, b]) => b - a).map(([color]) => color);
            const shapes: string[] = [];

            if (active) {
                shapes.push(`<circle cx="${center}" cy="${center}" r="${radius + MARKER_MARGIN - 1}" fill="none" stroke="${colors[0]}" stroke-opacity="0.35" stroke-width="${MARKER_MARGIN}"/>`);
            }

            shapes.push(`<circle cx="${center}" cy="${center}" r="${radius}" fill="${colors[0]}" stroke="#fff" stroke-width="2"/>`);

            if (count > 1) {
                shapes.push(countText(center, size, count));
            }

            if (colors.length > 1) {
                const offset = radius * 0.72;

                shapes.push(`<circle cx="${center + offset}" cy="${center - offset}" r="${size * 0.17}" fill="${colors[1]}" stroke="#fff" stroke-width="1.5"/>`);
            }

            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${canvas * 2}" height="${canvas * 2}" viewBox="0 0 ${canvas} ${canvas}">${shapes.join('')}</svg>`;

            return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
        }

        function countText(center: number, size: number, count: number): string {
            const fontSize = count < 10 ? size * 0.55 : size * 0.45;

            return `<text x="${center}" y="${center}" text-anchor="middle" dominant-baseline="central" fill="#fff" `
                + `font-family="system-ui, -apple-system, 'Segoe UI', sans-serif" font-size="${fontSize}" font-weight="700">${count}</text>`;
        }

        let pendingLayersControl: L.Control.Layers | null = null;

        function onMapReady(mapInstance: L.Map): void {
            leafletMap.value = mapInstance;

            if (pendingLayersControl) {
                publishLeafletMap(mapInstance.getContainer(), mapInstance, pendingLayersControl);
            }

            // LMap creates the Leaflet instance asynchronously, so the first load must wait for it.
            const waitingForGeolocation = tryUseUserLocation();

            if (!waitingForGeolocation) {
                doRefreshMarkers();
            }
        }

        function onLayersControlReady(layersControlInstance: L.Control.Layers): void {
            pendingLayersControl = layersControlInstance;

            if (leafletMap.value) {
                publishLeafletMap(leafletMap.value.getContainer(), leafletMap.value as L.Map, layersControlInstance);
            }
        }

        onBeforeUnmount(() => {
            if (refreshDebounceTimer !== null) {
                window.clearTimeout(refreshDebounceTimer);
            }

            if (leafletMap.value) {
                unpublishLeafletMap(leafletMap.value.getContainer());
            }
        });

        return {
            map,
            zoom,
            loading,
            activeMarker,
            dateRange,
            estateType,
            landType,
            tilesReady,
            centerLatLng,
            zoomTooSmall,
            zoomLabel,
            hint,
            updateCenterPoint,
            updateZoom,
            boundsChanged,
            closePanel,
            pointClicked,
            dateRangeChange,
            estateTypeChange,
            landTypeChange,
            onMapReady,
            onLayersControlReady,
        };
    },
});
</script>
